import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from './ui/carousel';
import { Heart, Users, Award, Target, Eye, Star, CheckCircle, Calendar, Building, Sun, HandHeart } from 'lucide-react';
import { api, getPublicSiteContent, getLeadershipTeam, getDetailedPageSections, getManagementMessages } from '../api';
import Header from './Header';
import Footer from './Footer';
import FundsTransparencySection from './FundsTransparencySection';

function normPersonName(s) {
  if (!s) return '';
  return s
    .toLowerCase()
    .replace(/\b(mr|mrs|ms|miss|dr|prof)\.?\s+/gi, '')
    .replace(/\./g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Match CMS management message to a leadership team member (by linked id, then by author name). */
function findManagementMessageForMember(member, messages) {
  if (!messages?.length) return null;
  const mid = member.id || member._id;
  if (mid) {
    const byId = messages.find((m) => m.leadership_member_id && m.leadership_member_id === mid);
    if (byId) return byId;
  }
  const mn = normPersonName(member.name);
  if (!mn) return null;
  const byExact = messages.find((m) => normPersonName(m.author_name || '') === mn);
  if (byExact) return byExact;
  return (
    messages.find(
      (m) =>
        normPersonName(m.author_name || '').length >= 4 &&
        (mn.includes(normPersonName(m.author_name || '')) ||
          normPersonName(m.author_name || '').includes(mn))
    ) || null
  );
}

const About = () => {
  // Site content state
  const [siteContent, setSiteContent] = useState({});
  // Leadership team state
  const [teamMembers, setTeamMembers] = useState([]);
  // Impact statistics state
  const [impactStats, setImpactStats] = useState({});
  // Detailed page sections state
  const [pageSections, setPageSections] = useState([]);
  const [managementMessages, setManagementMessages] = useState([]);

  // Load site content and leadership team on component mount
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load site content
        const backendContent = await getPublicSiteContent();
        if (backendContent.content && Object.keys(backendContent.content).length > 0) {
          setSiteContent(backendContent.content);
        } else {
          // Set empty object if no site content available
          setSiteContent({});
        }

        // Load leadership team
        const teamData = await getLeadershipTeam();
        if (teamData.members && teamData.members.length > 0) {
          setTeamMembers(teamData.members);
        } else {
          // Fallback to default team members if no data in backend
          setTeamMembers([
            {
              name: "Mrs. Swati Ingole",
              role: "Founder & Director",
              image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&crop=face",
              description: "Visionary leader with over 15 years of experience in social development and community empowerment."
            },
            {
              name: "Dr. Rajesh Kumar",
              role: "Senior Medical Advisor",
              image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=400&fit=crop&crop=face",
              description: "Leading geriatrician specializing in senior citizen healthcare and physiotherapy services."
            },
            {
              name: "Ms. Priya Sharma",
              role: "Training Program Manager",
              image: "https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?w=400&h=400&fit=crop&crop=face",
              description: "Expert in vocational training with focus on youth skill development and employment placement."
            }
          ]);
        }

        // Load impact statistics
        try {
          const statsData = await api.getImpactStats();
          if (statsData && Object.keys(statsData).length > 0) {
            setImpactStats(statsData);
          }
        } catch (statsError) {
          console.log('Using fallback impact statistics');
        }

        // Load detailed page sections
        try {
          const sectionsData = await getDetailedPageSections('about');
          setPageSections(sectionsData.sections || []);
        } catch (sectionsError) {
          console.log('Using empty page sections');
        }

        try {
          const msgData = await getManagementMessages();
          setManagementMessages(msgData.messages || []);
        } catch {
          setManagementMessages([]);
        }
      } catch (error) {
        console.log('Using fallback data for site content and leadership team');
        setSiteContent({});
        // Set fallback team members
        setTeamMembers([
          {
            name: "Mrs. Swati Ingole",
            role: "Founder & Director",
            image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop&crop=face",
            description: "Visionary leader with over 15 years of experience in social development and community empowerment."
          },
          {
            name: "Dr. Rajesh Kumar",
            role: "Senior Medical Advisor",
            image: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=400&fit=crop&crop=face",
            description: "Leading geriatrician specializing in senior citizen healthcare and physiotherapy services."
          },
          {
            name: "Ms. Priya Sharma",
            role: "Training Program Manager",
            image: "https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?w=400&h=400&fit=crop&crop=face",
            description: "Expert in vocational training with focus on youth skill development and employment placement."
          }
        ]);
        try {
          const msgData = await getManagementMessages();
          setManagementMessages(msgData.messages || []);
        } catch {
          setManagementMessages([]);
        }
      }
    };
    
    loadData();
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Compassion",
      description: "We approach every individual with empathy and understanding, recognizing their unique dignity and worth."
    },
    {
      icon: Users,
      title: "Community",
      description: "We believe in the power of community support and collective action to create lasting change."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We strive for the highest standards in our programs and services, ensuring quality outcomes."
    },
    {
      icon: Target,
      title: "Impact",
      description: "We focus on measurable results that create meaningful improvements in people's lives."
    }
  ];

  // Timeline/milestones now managed through database page sections

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-yellow-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {siteContent.about?.hero?.title || "About Shield Foundation"}
          </h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            {siteContent.about?.hero?.subtitle || "Our Story of Impact and Transformation"}
          </p>
          {siteContent.about?.hero?.description && (
            <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-4">
              {siteContent.about?.hero?.description}
            </p>
          )}
        </div>
      </section>

      {/* Our Story */}
      <section className="relative overflow-hidden bg-gradient-to-br from-amber-50/90 via-stone-50 to-yellow-50/80 py-14 md:py-16">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          aria-hidden
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(251, 191, 36, 0.15) 0%, transparent 45%), radial-gradient(circle at 85% 70%, rgba(180, 83, 9, 0.08) 0%, transparent 50%)',
          }}
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 md:grid-cols-2 md:gap-12 lg:gap-16">
            <div className="md:pr-4">
              <h2 className="mb-5 font-serif text-4xl font-semibold tracking-tight text-amber-950 md:text-5xl">
                {siteContent.about?.story?.title || 'Our Story'}
              </h2>
              <p className="text-lg leading-relaxed text-stone-700">
                {siteContent.about?.story?.content ||
                  `Shield Foundation was founded by Mrs. Swati Ingole with a clear mission: "To add life to years – 
                equipping and empowering communities so that every individual, especially the elderly and vulnerable, 
                can live with dignity till the end of life irrespective of socio-economic status."`}
              </p>
            </div>

            <div className="relative mx-auto flex w-full max-w-md justify-center md:mx-0 md:max-w-lg md:justify-end">
              <div
                className="absolute -left-1 top-1/2 z-10 hidden -translate-y-1/2 flex-col gap-5 text-amber-800/60 md:flex lg:-left-6"
                aria-hidden
              >
                <HandHeart className="h-9 w-9 stroke-[1.25]" />
                <Sun className="h-9 w-9 stroke-[1.25]" />
              </div>

              <div className="relative w-full">
                <div className="rounded-[1.75rem] bg-gradient-to-br from-amber-400 via-amber-500 to-amber-800 p-[3px] shadow-[0_20px_50px_-12px_rgba(180,83,9,0.35)]">
                  <div className="overflow-hidden rounded-[1.6rem] bg-stone-100">
                    <img
                      src={siteContent.about?.story?.image || '/images/swati-photo.png'}
                      alt="Mrs. Swati Ingole, Founder of Shield Foundation"
                      className="block h-auto max-h-[min(460px,62vh)] w-full object-contain"
                    />
                  </div>
                </div>

                <div
                  className="absolute -bottom-4 -left-3 z-20 flex h-[7.25rem] w-[7.25rem] flex-col items-center justify-center rounded-full bg-gradient-to-br from-amber-300 via-amber-500 to-amber-700 p-3 text-center shadow-lg ring-4 ring-amber-50/90 sm:-bottom-5 sm:-left-5 sm:h-32 sm:w-32"
                  role="presentation"
                >
                  <span className="font-serif text-xl font-bold leading-none text-amber-950 sm:text-2xl">
                    {siteContent.about?.story?.highlightBox?.text || '16+'}
                  </span>
                  <span className="mt-0.5 text-[9px] font-semibold uppercase leading-tight tracking-wide text-amber-950/90 sm:text-[10px]">
                    Years
                  </span>
                  <span className="mt-1 max-w-[5.5rem] text-[8px] font-medium uppercase leading-snug tracking-wide text-amber-950/85 sm:text-[9px]">
                    {siteContent.about?.story?.highlightBox?.subtext || 'Serving Communities'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision & Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Foundation</h2>
            <p className="text-xl text-gray-600">The principles that guide our work</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {/* Mission */}
            <Card className="h-full">
              <CardHeader>
                <Target className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle className="text-2xl text-blue-600">
                  {siteContent.about?.mission?.title || "Mission"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {siteContent.about?.mission?.content || `To add life to years – equipping and empowering communities so that every individual, 
                  especially the elderly and vulnerable, can live with dignity till the end of life 
                  irrespective of socio-economic status.`}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="h-full">
              <CardHeader>
                <Eye className="h-12 w-12 text-yellow-500 mb-4" />
                <CardTitle className="text-2xl text-yellow-600">
                  {siteContent.about?.vision?.title || "Vision"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  {siteContent.about?.vision?.content || `A society where every individual has access to opportunities for skill development, 
                  meaningful employment, and comprehensive care that ensures dignity and well-being 
                  throughout their life journey.`}
                </p>
              </CardContent>
            </Card>

            {/* Values */}
            <Card className="h-full">
              <CardHeader>
                <Star className="h-12 w-12 text-blue-600 mb-4" />
                <CardTitle className="text-2xl text-blue-600">Values</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 leading-relaxed">
                  Compassion, Excellence, Integrity, Community Partnership, and Sustainable Impact 
                  drive everything we do, ensuring that our work creates lasting positive change 
                  in the communities we serve.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Core Values Detail */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="p-6">
                    <IconComponent className="h-10 w-10 text-blue-600 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Leadership Team</h2>
            <p className="text-xl text-gray-600">Dedicated professionals committed to our mission</p>
          </div>
          
          {/* Group team members by category */}
          {(() => {
            // Group team members by category
            const groupedMembers = teamMembers.reduce((acc, member) => {
              const category = member.category || 'Trustee';
              if (!acc[category]) {
                acc[category] = [];
              }
              acc[category].push(member);
              return acc;
            }, {});

            // Define category order and display names
            const categoryOrder = ['Trustee', 'Programs Team', 'Admin & Finance'];
            const categoryDisplayNames = {
              'Trustee': 'Trustee',
              'Programs Team': 'Programs Team',
              'Admin & Finance': 'Admin & Finance'
            };

            return categoryOrder.map((categoryKey) => {
              const members = groupedMembers[categoryKey] || [];
              if (members.length === 0) return null;

              return (
                <div key={categoryKey} className="mb-16">
                  <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                    {categoryDisplayNames[categoryKey]}
                  </h3>
                  <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-8">
                    {members.map((member, index) => {
                      const linkedMessage = findManagementMessageForMember(member, managementMessages);
                      const memberKey = member.id || member._id || `${categoryKey}-${index}`;
                      return (
                        <Card key={memberKey} className="text-center hover:shadow-lg transition-shadow duration-300 flex flex-col">
                          <CardContent className="flex flex-col p-8 flex-1">
                            <img
                              src={member.image}
                              alt={member.name}
                              className="w-32 h-32 rounded-full mx-auto mb-6 object-cover"
                              onError={(e) => {
                                e.target.src = "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face";
                              }}
                            />
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">{member.name}</h3>
                            <Badge className="mb-4 bg-yellow-400 text-black hover:bg-yellow-500 mx-auto">
                              {member.role}
                            </Badge>
                            {member.description && (
                              <p className="text-gray-600 text-sm leading-relaxed flex-1">{member.description}</p>
                            )}
                            {linkedMessage && (
                              <Dialog>
                                <DialogTrigger asChild>
                                  <button
                                    type="button"
                                    className="mt-4 w-full shrink-0 border-0 bg-transparent p-0 text-center text-sm font-medium text-slate-400 underline-offset-4 transition-colors hover:text-blue-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 rounded-sm"
                                  >
                                    Read their message<span aria-hidden="true"> →</span>
                                  </button>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg sm:max-w-xl max-h-[min(85vh,640px)] flex flex-col gap-0 p-0 sm:rounded-xl overflow-hidden">
                                  <DialogHeader className="space-y-1 border-b border-border/60 bg-muted/30 px-6 py-4 text-left">
                                    <DialogTitle className="text-lg leading-snug pr-8">
                                      {linkedMessage.title || 'Message from leadership'}
                                    </DialogTitle>
                                    <DialogDescription className="text-sm">
                                      {[linkedMessage.author_name, linkedMessage.author_role].filter(Boolean).join(' — ') ||
                                        member.name}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 text-sm leading-relaxed text-foreground whitespace-pre-line">
                                    {linkedMessage.message}
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}

          {/* Fallback if no team members */}
          {teamMembers.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No team members available at this time.</p>
            </div>
          )}
        </div>
      </section>

      {/* Dynamic Page Sections */}
      {pageSections.length > 0 && (
        <div>
          {pageSections.map((section, index) => {
            // Special handling for "Our Journey" section - Timeline Layout
            if (section.section === 'journey') {
              return (
                <section key={section.id} className="py-20 bg-gray-50">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                      <h2 className="text-4xl font-bold text-gray-900 mb-4">{section.title}</h2>
                      {section.content.text && (
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                          {section.content.text}
                        </p>
                      )}
                    </div>
                    
                    {/* Timeline Layout */}
                    {section.content.items && section.content.items.length > 0 && (
                      <div className="relative max-w-6xl mx-auto">
                        {/* Center timeline line */}
                        <div className="absolute left-1/2 transform -translate-x-0.5 top-0 bottom-0 w-1 bg-blue-600 hidden md:block"></div>
                        
                        <div className="space-y-12">
                          {section.content.items.map((milestone, milestoneIndex) => (
                            <div key={milestoneIndex} className={`relative flex items-center ${milestoneIndex % 2 === 0 ? 'md:justify-start' : 'md:justify-end'}`}>
                              {/* Timeline dot */}
                              <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-yellow-400 rounded-full border-4 border-white shadow-lg z-10 hidden md:block"></div>
                              
                              <div className={`w-full md:w-5/12 ${milestoneIndex % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                                <Card className={`hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${
                                  milestoneIndex % 2 === 0 
                                    ? 'md:ml-0' 
                                    : 'md:mr-0'
                                }`}>
                                  <CardContent className="p-6 relative">
                                    {/* Arrow pointing to timeline */}
                                    <div className={`absolute top-8 hidden md:block ${
                                      milestoneIndex % 2 === 0 
                                        ? 'right-0 transform translate-x-full' 
                                        : 'left-0 transform -translate-x-full'
                                    }`}>
                                      <div className={`w-0 h-0 ${
                                        milestoneIndex % 2 === 0
                                          ? 'border-l-[20px] border-l-white border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent'
                                          : 'border-r-[20px] border-r-white border-t-[10px] border-t-transparent border-b-[10px] border-b-transparent'
                                      }`}></div>
                                    </div>
                                    
                                    <div className="flex items-center mb-4">
                                      <Badge className="bg-blue-600 text-white text-sm px-3 py-1 mr-3">
                                        {milestone.title?.split(' - ')[0] || `Step ${milestoneIndex + 1}`}
                                      </Badge>
                                      <Calendar className="h-5 w-5 text-blue-600" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                                      {milestone.title?.split(' - ')[1] || milestone.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                                  </CardContent>
                                </Card>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Start and End markers */}
                        <div className="absolute left-1/2 transform -translate-x-1/2 -top-4 w-4 h-4 bg-blue-600 rounded-full hidden md:block"></div>
                        <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-4 w-4 h-4 bg-blue-600 rounded-full hidden md:block"></div>
                      </div>
                    )}
                  </div>
                </section>
              );
            }
            
            // Special handling for "Our Partners" section
            if (section.section === 'partners') {
              return (
                <section key={section.id} className="py-20">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                      <h2 className="text-4xl font-bold text-gray-900 mb-4">{section.title}</h2>
                      {section.content.text && (
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                          {section.content.text}
                        </p>
                      )}
                    </div>
                    
                    {section.content.items && section.content.items.length > 0 && (
                      section.content.metadata?.display_as_carousel === true ? (
                        <div className="w-full max-w-6xl mx-auto relative">
                          <Carousel className="w-full">
                            <CarouselContent className="-ml-2 md:-ml-4">
                              {section.content.items.map((partner, partnerIndex) => (
                                <CarouselItem key={partnerIndex} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                                  <Card className="text-center border-2 border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300 h-full">
                                    <CardContent className="p-6">
                                      {partner.image_url && partner.image_url.trim() !== '' ? (
                                        <div className="mb-4 flex items-center justify-center bg-gray-50 rounded-lg p-4" style={{ minHeight: '120px', maxHeight: '120px' }}>
                                          <img 
                                            src={partner.image_url} 
                                            alt={partner.title || 'Partner logo'}
                                            className="max-w-full max-h-full object-contain"
                                            style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100px' }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        </div>
                                      ) : (
                                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                          <Building className="h-8 w-8 text-blue-600" />
                                        </div>
                                      )}
                                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {partner.title}
                                      </h3>
                                      {partner.description && (
                                        <p className="text-gray-600 text-sm leading-relaxed">
                                          {partner.description}
                                        </p>
                                      )}
                                    </CardContent>
                                  </Card>
                                </CarouselItem>
                              ))}
                            </CarouselContent>
                            <CarouselPrevious className="left-0 md:-left-12" />
                            <CarouselNext className="right-0 md:-right-12" />
                          </Carousel>
                        </div>
                      ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                          {section.content.items.map((partner, partnerIndex) => (
                            <Card key={partnerIndex} className="text-center border-2 border-blue-100 hover:border-blue-300 hover:shadow-lg transition-all duration-300">
                              <CardContent className="p-6">
                                {partner.image_url && partner.image_url.trim() !== '' ? (
                                  <div className="mb-4 flex items-center justify-center bg-gray-50 rounded-lg p-4" style={{ minHeight: '120px', maxHeight: '120px' }}>
                                    <img 
                                      src={partner.image_url} 
                                      alt={partner.title || 'Partner logo'}
                                      className="max-w-full max-h-full object-contain"
                                      style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '100px' }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="mb-4">
                                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                      <Building className="h-8 w-8 text-blue-600" />
                                    </div>
                                  </div>
                                )}
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                  {partner.title}
                                </h3>
                                {partner.description && (
                                  <p className="text-gray-600 text-sm leading-relaxed">
                                    {partner.description}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )
                    )}
                  </div>
                </section>
              );
            }
            
            // Default layout for other sections
            return (
              <section key={section.id} className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                  <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {section.title}
                    </h2>
                  </div>

                  <div className="space-y-8">
                    {/* Text Content */}
                    {section.content.text && (
                      <div className="prose max-w-none text-lg text-gray-600 leading-relaxed text-center">
                        <p>{section.content.text}</p>
                      </div>
                    )}

                    {/* Featured Image */}
                    {section.content.image_url && (
                      <div className="text-center">
                        <img 
                          src={section.content.image_url} 
                          alt={section.title}
                          className="max-w-full h-auto rounded-lg shadow-lg mx-auto"
                        />
                      </div>
                    )}

                    {/* Dynamic Items */}
                    {section.content.items && section.content.items.length > 0 && (
                      section.content.metadata?.display_as_carousel === true ? (
                        <div className="w-full max-w-6xl mx-auto relative">
                          <Carousel className="w-full">
                            <CarouselContent className="-ml-2 md:-ml-4">
                              {section.content.items.map((item, itemIndex) => (
                                <CarouselItem key={itemIndex} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                                  <div className="bg-white p-6 rounded-lg shadow-md h-full">
                                    {item.image_url && item.image_url.trim() !== '' && (
                                      <div className="mb-4 flex items-center justify-center bg-gray-50 rounded-lg p-4" style={{ minHeight: '160px', maxHeight: '160px' }}>
                                        <img 
                                          src={item.image_url} 
                                          alt={item.title || 'Item image'}
                                          className="max-w-full max-h-full object-contain"
                                          style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '132px' }}
                                          onError={(e) => {
                                            console.error('Failed to load image:', item.image_url, 'for item:', item.title);
                                            e.target.style.display = 'none';
                                          }}
                                          onLoad={() => {
                                            console.log('Image loaded successfully:', item.image_url);
                                          }}
                                        />
                                      </div>
                                    )}
                                    {item.title && (
                                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                        {item.title}
                                      </h3>
                                    )}
                                    {item.description && (
                                      <p className="text-gray-600">
                                        {item.description}
                                      </p>
                                    )}
                                  </div>
                                </CarouselItem>
                              ))}
                            </CarouselContent>
                            <CarouselPrevious className="left-0 md:-left-12" />
                            <CarouselNext className="right-0 md:-right-12" />
                          </Carousel>
                        </div>
                      ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {section.content.items.map((item, itemIndex) => {
                            // Debug: log item data
                            if (item.image_url) {
                              console.log('Item with image:', { index: itemIndex, title: item.title, image_url: item.image_url });
                            }
                            return (
                            <div key={itemIndex} className="bg-white p-6 rounded-lg shadow-md">
                              {item.image_url && item.image_url.trim() !== '' && (
                                <div className="mb-4 flex items-center justify-center bg-gray-50 rounded-lg p-4" style={{ minHeight: '160px', maxHeight: '160px' }}>
                                  <img 
                                    src={item.image_url} 
                                    alt={item.title || 'Item image'}
                                    className="max-w-full max-h-full object-contain"
                                    style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: '100%', maxHeight: '132px' }}
                                    onError={(e) => {
                                      console.error('Failed to load image:', item.image_url, 'for item:', item.title);
                                      e.target.style.display = 'none';
                                    }}
                                    onLoad={() => {
                                      console.log('Image loaded successfully:', item.image_url);
                                    }}
                                  />
                                </div>
                              )}
                              {item.title && (
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                                  {item.title}
                                </h3>
                              )}
                              {item.description && (
                                <p className="text-gray-600">
                                  {item.description}
                                </p>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      )
                    )}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}

      {/* Impact Summary */}
      <FundsTransparencySection />

      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-black mb-4">Our Impact Today</h2>
            <p className="text-xl text-black/80">Lives transformed, communities empowered</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-black mb-2">
                {impactStats.youthTrained?.toLocaleString() || 0}+
              </div>
              <div className="text-black/70">Youth Trained</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-black mb-2">
                {impactStats.youthPlaced?.toLocaleString() || 0}+
              </div>
              <div className="text-black/70">Youth Placed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-black mb-2">
                {impactStats.seniorsSupported?.toLocaleString() || 0}+
              </div>
              <div className="text-black/70">Seniors Supported</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-black mb-2">
                {impactStats.womenEmpowered?.toLocaleString() || 0}+
              </div>
              <div className="text-black/70">Women Empowered</div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
